import React from 'react';
import {
	Typography,
	FormControl,
	InputLabel,
	MenuItem,
	InputAdornment,
} from '@mui/material';
import { SearchRounded, PersonRounded } from '@mui/icons-material';
import { EResource } from '../../types/departamento';
import { IMiMateria, IMiRecurso } from '../../types/miRecurso';
import RecursoMateriaCard from './RecursoMateriaCard';
import {
	PageContainer,
	DetailHeader,
	DetailHeaderLeft,
	DocenteChip,
	FiltersContainer,
	SearchField,
	FilterSelect,
	ResultsHeader,
	RecursosGrid,
	EmptyStateContainer,
} from './styles';

interface MateriaRecursosPageProps {
	materia: IMiMateria;
	recursos: IMiRecurso[];
	searchQuery: string;
	onSearchChange: (query: string) => void;
	selectedFileType: EResource | 'all';
	onFileTypeChange: (type: EResource | 'all') => void;
	onDownload: (recurso: IMiRecurso) => void;
}

const MateriaRecursosPage: React.FC<MateriaRecursosPageProps> = ({
	materia,
	recursos,
	searchQuery,
	onSearchChange,
	selectedFileType,
	onFileTypeChange,
	onDownload,
}) => {
	return (
		<PageContainer>
			{/* Materia header */}
			<DetailHeader>
				<DetailHeaderLeft>
					<Typography variant='h6' fontWeight={700}>
						{materia.nombre}
					</Typography>
					<DocenteChip
						icon={<PersonRounded style={{ fontSize: '0.9rem' }} />}
						label={materia.docenteNombre}
						size='small'
					/>
				</DetailHeaderLeft>
				<Typography variant='body2' color='text.secondary'>
					{materia.resourceCount}{' '}
					{materia.resourceCount === 1 ? 'recurso disponible' : 'recursos disponibles'}
				</Typography>
			</DetailHeader>

			{/* Search + file type filter */}
			<FiltersContainer>
				<SearchField
					fullWidth
					placeholder='Buscar por nombre de archivo...'
					value={searchQuery}
					onChange={(e) => onSearchChange(e.target.value)}
					InputProps={{
						startAdornment: (
							<InputAdornment position='start'>
								<SearchRounded />
							</InputAdornment>
						),
					}}
				/>
				<FormControl>
					<InputLabel id='filetype-label'>Tipo de archivo</InputLabel>
					<FilterSelect
						labelId='filetype-label'
						value={selectedFileType}
						label='Tipo de archivo'
						onChange={(e) => onFileTypeChange(e.target.value as EResource | 'all')}
					>
						<MenuItem value='all'>Todos los tipos</MenuItem>
						<MenuItem value={EResource.pdf}>PDF</MenuItem>
						<MenuItem value={EResource.docx}>Word</MenuItem>
						<MenuItem value={EResource.pptx}>PowerPoint</MenuItem>
						<MenuItem value={EResource.xlsx}>Excel</MenuItem>
						<MenuItem value={EResource.video}>Video</MenuItem>
						<MenuItem value={EResource.imagen}>Imagen</MenuItem>
					</FilterSelect>
				</FormControl>
			</FiltersContainer>

			{/* Results count */}
			<ResultsHeader>
				<Typography variant='h6' color='text.primary'>
					{recursos.length} archivo{recursos.length !== 1 ? 's' : ''}{' '}
					encontrado{recursos.length !== 1 ? 's' : ''}
				</Typography>
				<Typography variant='body2' color='text.secondary'>
					Ordenar por: <strong>Más reciente</strong>
				</Typography>
			</ResultsHeader>

			{/* Card grid */}
			{recursos.length > 0 ? (
				<RecursosGrid>
					{recursos.map((recurso) => (
						<RecursoMateriaCard
							key={recurso.id}
							recurso={recurso}
							docenteNombre={materia.docenteNombre}
							onDownload={onDownload}
						/>
					))}
				</RecursosGrid>
			) : (
				<EmptyStateContainer>
					<Typography variant='h6' color='text.secondary' gutterBottom>
						No hay recursos disponibles
					</Typography>
					<Typography variant='body2' color='text.secondary'>
						{searchQuery || selectedFileType !== 'all'
							? 'No se encontraron archivos con esos criterios. Prueba ajustando los filtros.'
							: 'Tu docente aún no ha publicado recursos para esta materia.'}
					</Typography>
				</EmptyStateContainer>
			)}
		</PageContainer>
	);
};

export default MateriaRecursosPage;
